import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@emotion/react';
import * as Sentry from '@sentry/react';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import airbridge from 'airbridge-web-sdk-loader';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import 'aws-amplify/auth/enable-oauth-listener';
import { defaultStorage } from 'aws-amplify/utils';
import { Modal, SelectOptionsModal } from '~/components/atomic';
import '~/constants/sentry.ts';
import GlobalStyle from '~/styles/Global';
import { DarkTheme } from '~/styles/themes/theme.ts';
import { ToastProvider } from '~/context';
import App from './App.tsx';
import { AIRBRIDGE } from './constants/airbridge.ts';
import { authConfig } from './constants/awsAuthService.ts';
import useUserStore from './store/useUserStore.ts';
import { isEventLoggingValid } from './utils/eventTracker.ts';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      Sentry.captureException(error);
    },
  }),
});

if (authConfig) cognitoUserPoolsTokenProvider.setAuthConfig(authConfig);
cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage);
Amplify.configure(
  {
    Auth: authConfig,
  },
  { Auth: { tokenProvider: cognitoUserPoolsTokenProvider } },
);

const Root = () => {
  const theme = DarkTheme;
  const { email } = useUserStore();

  if (email && isEventLoggingValid(email)) {
    airbridge.init({
      app: AIRBRIDGE.APP_NAME,
      webToken: AIRBRIDGE.WEB_TOKEN,
    });
  } else if (email && !isEventLoggingValid(email)) {
    console.log('Airbridge tracking DISABLED');
  }

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <App />
          <Modal />
          <SelectOptionsModal />
          {/* <ReactQueryDevtools initialIsOpen={false} /> */}
          <ToastProvider />
        </ThemeProvider>
      </QueryClientProvider>
    </>
  );
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <Root />,
);
